import { lazy, Suspense } from 'react'
import { RouterProvider, Outlet, createBrowserRouter } from 'react-router-dom'
import {
  routerMiddleware,
  awaitUser,
  requireAuth,
  dashboardRedirect,
} from './middleware.js'
import ChunkLoadErrorBoundary from '../components/ChunkLoadErrorBoundary.tsx'
import { injectAddToast as injectAddToastOld } from '../firestore.js'
import { injectAddToast as injectAddToastNew } from '../utils/api-error-handler.js'
import { toastAtom } from '../store/toast.store.js'
import { useSetAtom } from 'jotai'
import ToastWrapper from '../components/toast/ToastWrapper.jsx'
import { UpgradeDialog } from '../components/UpgradeDialog.tsx'
import { UpdateBanner } from '../features/pwa'
import PageViewTracker from '../components/renderless/PageViewTracker.jsx'
import Login from '../routes/login'
import Signup from '../routes/signup'
import Verify from '../routes/verify'
import Create from '../routes/create'
import Profile from '../components/dashboard/Profile'
import Playground from '../routes/playground'
import { RundownRoute } from '@/features/rundown-session/RundownRoute.tsx'
import { RundownPage } from '@/features/rundown/RundownPage.tsx'
import { RundownOutputPage } from '@/features/rundown-output/RundownOutputPage.tsx'

import Code from '../routes/code.jsx'
import Team from '../components/dashboard/Team.tsx'
import AcceptInvite from '../routes/accept-invite.jsx'
import Logout from '../routes/logout.jsx'
import Reset from '../routes/reset.jsx'
import Checkout from '../routes/checkout.jsx'
import CheckoutPre from '../routes/checkout-pre.jsx'
import CheckoutSuccess from '../routes/checkout-success.jsx'
import Billing from '../components/dashboard/Billing.jsx'
import DashboardParent from '../routes/dashboard/parent.jsx'
import DashboardMain from '../routes/dashboard/dashboard-main.jsx'
import DashboardCheck from '../routes/dashboard/dashboard-check.jsx'
import API from '../components/dashboard/Api.tsx'
import Event from '../components/dashboard/Event.jsx'
import CreateEvent from '../routes/create-event.jsx'
import PublicEvent from '../routes/public-event.jsx'
import Archive from '../components/dashboard/Archive.jsx'
import PaddleRetainTest from '../routes/paddle-retain-test.jsx'
import { Layout } from '../components/layout.js'
import { RouteGuard } from '../features/permissions/ui/route-guard.tsx'
import { UserRole } from '~shared/types'

const CreateTeam = lazy(() => import('../routes/create-team'))
const CsvImportMain = lazy(
  () => import('../routes/dashboard/csv-import/csv-import-main.js'),
)
const CsvImportPreview = lazy(
  () => import('../routes/dashboard/csv-import/csv-import-preview.js'),
)
const CsvImportReview = lazy(
  () => import('../routes/dashboard/csv-import/csv-import-review.js'),
)

const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    errorElement: <ChunkLoadErrorBoundary />,
    // HydrateFallback renders on first load while the matched route's loader
    // runs. React Router renders nothing until a loader resolves, so routes
    // gated on auth (eg. `requireAuth`) would show an empty page.
    // Routes that owns their own loading state should not set `loader:`.
    HydrateFallback: () => (
      <Layout
        title="Loading..."
        loading
      />
    ),
    children: [
      {
        path: '/',
        element: <DashboardCheck />,
        loader: routerMiddleware(requireAuth),
      },
      {
        path: 'code',
        element: <Code />,
      },
      {
        path: 'playground',
        element: <Playground />,
      },
      {
        path: 'dashboard',
        element: <DashboardCheck />,
      },
      {
        path: 'dashboard/:teamId',
        element: <DashboardParent />,
        loader: routerMiddleware(requireAuth),
        children: [
          {
            path: '',
            element: <DashboardMain />,
          },
          {
            path: 'event/:eventId',
            element: (
              <RouteGuard role={UserRole.EVENT_VIEWER}>
                <Event />
              </RouteGuard>
            ),
          },
          {
            path: 'archive',
            element: (
              <RouteGuard role={UserRole.TEAM_EDITOR}>
                <Archive />
              </RouteGuard>
            ),
          },
          {
            path: 'team',
            element: (
              <RouteGuard role={UserRole.TEAM_ADMIN}>
                <Team />
              </RouteGuard>
            ),
          },
          {
            path: 'api',
            element: (
              <RouteGuard role={UserRole.TEAM_ADMIN}>
                <API />
              </RouteGuard>
            ),
          },
          {
            path: 'billing',
            element: (
              <RouteGuard role={UserRole.TEAM_ADMIN}>
                <Billing />
              </RouteGuard>
            ),
          },
          {
            path: 'profile',
            element: <Profile />,
          },
          {
            path: 'import',
            element: (
              <RouteGuard role={UserRole.TEAM_EDITOR}>
                <Suspense
                  fallback={
                    <Layout
                      title="Loading..."
                      loading
                    />
                  }
                >
                  <Outlet />
                </Suspense>
              </RouteGuard>
            ),
            children: [
              {
                path: '',
                element: <CsvImportMain />,
              },
              {
                path: 'preview',
                element: <CsvImportPreview />,
              },
              {
                path: 'review',
                element: <CsvImportReview />,
              },
            ],
          },
        ],
      },
      {
        path: 'verify',
        element: <Verify />,
        loader: routerMiddleware(requireAuth),
      },
      {
        path: 'event/:eventId',
        element: <PublicEvent />,
      },
      {
        path: 'rundown/:rundownId',
        element: <RundownRoute />,
        // No loader: rundown-session owns the loading state. Adding one brings
        // back the root HydrateFallback, which renders the same loading screen
        // and restarts the progress bar when RundownRoute mounts.
        children: [
          {
            path: '',
            element: <RundownPage />,
          },
          {
            path: 'output',
            element: <RundownOutputPage />,
          },
        ],
      },
      {
        path: 'checkout',
        element: <Checkout />,
        loader: routerMiddleware(requireAuth),
      },
      {
        path: 'checkout-pre',
        element: <CheckoutPre />,
        loader: routerMiddleware(requireAuth),
      },
      {
        path: 'checkout-success',
        element: <CheckoutSuccess />,
        loader: routerMiddleware(requireAuth),
      },
      {
        path: 'accept-invite',
        element: <AcceptInvite />,
        loader: routerMiddleware(requireAuth),
      },
      {
        path: 'create',
        element: <Create />,
        loader: routerMiddleware(awaitUser),
      },
      {
        path: 'create-team',
        element: (
          <Suspense
            fallback={
              <Layout
                title="Loading..."
                loading
              />
            }
          >
            <CreateTeam />
          </Suspense>
        ),
        loader: routerMiddleware(awaitUser),
      },
      {
        path: 'create-event',
        element: <CreateEvent />,
        loader: routerMiddleware(awaitUser),
      },
      {
        path: 'login',
        element: <Login />,
        loader: routerMiddleware(dashboardRedirect),
      },
      {
        path: 'logout',
        element: <Logout />,
      },
      {
        path: 'reset',
        element: <Reset />,
      },
      {
        path: 'signup',
        element: <Signup />,
        loader: routerMiddleware(dashboardRedirect),
      },
      {
        path: 'paddle-retain-test',
        element: <PaddleRetainTest />,
      },
    ],
  },
])

function Root() {
  const addToast = useSetAtom(toastAtom)

  injectAddToastOld(addToast)
  injectAddToastNew(addToast)

  return (
    <>
      <ToastWrapper />
      <PageViewTracker />

      <Outlet />

      <UpgradeDialog />
      <UpdateBanner />
    </>
  )
}

export default function Router() {
  return <RouterProvider router={router} />
}
